/**
 * The station-volumes palette (spec §5), in one place.
 *
 * Cut and fill are a *polarity*, not two categories, so they take a diverging
 * pair: opposite-temperature hues with a neutral — never a hue — at zero. The
 * values below are the validated steps from the spec's table; the dark steps
 * are chosen for the dark surface, not flipped from the light ones.
 *
 * Two consumers, one source:
 *  - `SV_TOKENS` is injected once by `StationVolumeDock` and gives the chart,
 *    the table and the cross-section `var(--sv-…)` to draw with.
 *  - `SV_COLORS` gives the 3D bands (Task 14) the same values as plain hex,
 *    because three.js materials cannot read CSS custom properties.
 *
 * This lives in the lib rather than in the dock so the dock's children never
 * have to import their own parent (ruling T10.2's reasoning, applied to the
 * palette as well as to the formatters).
 */

/**
 * The walk cursor is deliberately the alignment overlay's highlight amber —
 * it must be the *same* colour object in 2D and 3D. Keep in step with
 * `HIGHLIGHT` in `app/src/components/3D/AlignmentOverlay.tsx`. (Imported as a
 * literal rather than from that module because it pulls three.js in with it.)
 */
export const SV_WALK = '#ffb300';

export interface SvPalette {
  /**
   * The CUT ink, and it is the RED of the pair — the owner's ruling (T4,
   * closing F21): cut is red and fill is blue on every surface, from this
   * palette through the 3D bands and the section drawing to the per-station
   * PDF and its render. `@/lib/cutFillConvention` states the ruling and says
   * which SIGN is cut for each kind of run; these are the two colours it
   * names. Swapping them here would silently swap them everywhere, which is
   * the point of there being only one pair.
   */
  cut: string;
  cutWash: string;
  fill: string;
  fillWash: string;
  zero: string;
  /**
   * "Measured, and clear" — the third rung of the clearance K2 ladder, drawn
   * by the clearance chart's bars, its legend and the 3D corridor plates.
   *
   * It is NOT `zero`. `zero` is the diverging ramp's midpoint, a near-white
   * neutral chosen to *disappear*, which is exactly right for "the cut and the
   * fill balance here" and exactly wrong for "we swept this ground and it is
   * clear" — a claim that has to be visible on a light basemap at the bands'
   * 55 % alpha. The pale `zero` stays in use for the plates the sweep never
   * scored, which carry a hatch to say so.
   */
  clear: string;
  /**
   * "Nothing intruded, but a point sat inside the near-miss ladder" — the
   * second rung of the clearance K2 ladder, drawn by the chart's bars, its
   * legend and the 3D corridor plates.
   *
   * A data mark of its own, and NOT `warn` (finding M3): `warn` is documented
   * below as a status ink and is spent, in the very same header row, on the
   * "alignment changed" chip. One hue cannot be both "this measurement is
   * close to the limit" and "this result may be about the wrong stations".
   *
   * Its value is the CLEARANCE colour ramp's own boundary amber
   * (`colormaps.ts`'s `CLEARANCE` stop 4), so the near-miss bar, the near-miss
   * plate and the band of the point cloud they describe are one colour rather
   * than three adjacent ambers (finding M4). It is theme-independent for the
   * same reason the ramp is: the shader's gradient does not flip with the app
   * theme, and a legend that did would stop matching the cloud.
   */
  caution: string;
  /**
   * The two CROSSFALL series, left and right.
   *
   * Deliberately NOT `cut`/`fill`: those two are a POLARITY — one quantity
   * either side of zero — and the diverging pair is chosen so that the reader
   * cannot mistake one for the other on a chart where the sign IS the
   * meaning. Crossfall's two series are two SIDES OF THE ROAD, a category,
   * and both of them cross zero on a superelevated curve. Spending the
   * diverging pair here would tell the reader that the left lane is "cut" and
   * the right lane is "fill", which is not a statement about anything.
   *
   * The values are the first two members of `series` (Okabe-Ito blue and
   * vermillion), so the crossfall pane and a section overlay are drawn from
   * one colour-vision-safe set rather than two.
   */
  xfLeft: string;
  xfRight: string;
  haul: string;
  walk: string;
  surface: string;
  /** Status ink for warning chips — "alignment changed", the stale silhouette.
   *  Not a data mark: the K2 near-miss rung has `caution` of its own, and this
   *  one is sized for TEXT contrast (`.sv-chip--warn` colours the label), which
   *  is why it is not the ramp's amber. */
  warn: string;
  /**
   * Maximum-contrast ink AGAINST `surface`: the 3D hover outline, the
   * shader's hover brighten and the band tooltip's text. Like `warn` it is
   * not one of spec §5's data marks — but the hover outline was a hard-coded
   * white, which is invisible on a light-mode sky, and "which band am I
   * pointing at" has to survive both themes.
   */
  ink: string;
  /**
   * The REFERENCE overlay inks (section-overlays spec §3) — eight of them,
   * matching `MAX_SECTION_OVERLAYS`, so no two configured overlays ever share
   * a colour.
   *
   * The first SIX are the Okabe-Ito qualitative set — the one categorical
   * palette designed so that deuteranopes, protanopes and tritanopes can all
   * still tell the members apart — and the last two (a violet and a brown)
   * are ADDITIONS, because Okabe-Ito's own remaining members are a yellow and
   * a black that do not survive this chart's two surfaces. Beyond six, the
   * separation guarantee is ours, not Okabe-Ito's. The dark row is the set
   * restated for a dark surface, not the light row flipped.
   *
   * Colour-vision safety matters here more than on most charts: a reference
   * line's only identity IS its ink, since every one of them is drawn at the
   * same weight with the same geometry. That is also why the section's
   * forced-colours rules give each one a dash pattern — when the ink is taken
   * away, the identity has to survive somewhere.
   *
   * Not CSS custom properties: the ink travels through `SectionSeries.color`
   * into an SVG `stroke` and into the legend swatch, and both need the value
   * itself, not a `var()` the legend cannot compare against.
   */
  series: string[];
}

export const SV_COLORS: { light: SvPalette; dark: SvPalette } = {
  light: {
    cut: '#e34948',
    cutWash: '#e3494826',
    fill: '#2a78d6',
    fillWash: '#2a78d626',
    zero: '#f0efec',
    clear: '#8f8e85',
    caution: '#f2b705',
    xfLeft: '#0072b2',
    xfRight: '#d55e00',
    haul: '#52514e',
    walk: SV_WALK,
    surface: '#fcfcfb',
    warn: '#b45309',
    ink: '#0b0b0a',
    series: ['#0072b2', '#d55e00', '#009e73', '#cc79a7', '#e69f00', '#56b4e9', '#7b4fbf', '#8c564b'],
  },
  dark: {
    cut: '#e66767',
    cutWash: '#e6676733',
    fill: '#3987e5',
    fillWash: '#3987e533',
    zero: '#383835',
    clear: '#7d7c74',
    caution: '#f2b705',
    xfLeft: '#6cb4ee',
    xfRight: '#ff9e57',
    haul: '#c3c2b7',
    walk: SV_WALK,
    surface: '#1a1a19',
    warn: '#fbbf24',
    ink: '#ffffff',
    series: ['#6cb4ee', '#ff9e57', '#3fd39f', '#f0a3d0', '#ffd166', '#8ad3ff', '#b48cff', '#c9946b'],
  },
};

const vars = (c: SvPalette) => `
  --sv-cut:${c.cut};
  --sv-cut-wash:${c.cutWash};
  --sv-fill:${c.fill};
  --sv-fill-wash:${c.fillWash};
  /* The clearance K2 ladder's third rung — see \`SvPalette.clear\`. Emitted
     here so the chart's bars and legend and the 3D plates read ONE value. */
  --sv-clear:${c.clear};
  /* The ladder's second rung — see \`SvPalette.caution\`. Named for its role,
     not for \`warn\`, which is the status ink the same header row spends on
     "alignment changed". */
  --sv-caution:${c.caution};
  /* The two crossfall series — a CATEGORY (left lane / right lane), never the
     cut/fill polarity; see \`SvPalette.xfLeft\`. */
  --sv-xf-left:${c.xfLeft};
  --sv-xf-right:${c.xfRight};
  --sv-haul:${c.haul};
  /* The ink the cross-section draws its two surfaces in. Today it is the
     mass-haul neutral, and deliberately so — a surface line is a geometry
     mark, not a cut/fill category — but it is named for its own role so a
     later change to one does not silently move the other. */
  --sv-surface-ink:${c.haul};
  --sv-walk:${c.walk};
  --sv-surface:${c.surface};
  --sv-warn:${c.warn};
`;

/**
 * Light on the bare selector, dark redefined three ways so an explicit theme
 * always beats the system preference. `.dark .sv-root` is this app's own
 * strategy (Tailwind's `dark` variant is `&:is(.dark *)`, and `providers/
 * index.tsx` writes `light` or `dark` onto `<html>` from `useTheme`);
 * `[data-theme='dark']` is kept for the spec's contract and for anything
 * embedding the dock elsewhere.
 *
 * `--sv-grid` is `currentColor` at 10 % rather than a fixed ink, so gridlines
 * sit one step off whatever surface the dock is drawn on.
 */
export const SV_TOKENS = `
.sv-root,.sv-palette{${vars(SV_COLORS.light)}
  --sv-grid:color-mix(in srgb, currentColor 10%, transparent);
}
/* Only the dock paints itself with the palette. \`.sv-palette\` is the same
   variables with no surface of its own, for a widget that lives on someone
   else's background — the surface-difference panel's cut/fill figures. */
.sv-root{background:var(--sv-surface);}
/* \`html:not(.light)\` matters: \`providers/index.tsx\` writes the RESOLVED theme
   onto <html> as \`light\` or \`dark\`, so a user who picked light on a machine
   whose OS prefers dark used to get the dark dock on a light app — the system
   preference is only the fallback, never an override of an explicit choice. */
@media (prefers-color-scheme:dark){
  html:not(.light) .sv-root:not([data-theme='light']):not(.sv-light),
  html:not(.light) .sv-palette:not([data-theme='light']):not(.sv-light){${vars(SV_COLORS.dark)}}
}
[data-theme='dark'] .sv-root,
.sv-root[data-theme='dark'],
.dark .sv-root,
[data-theme='dark'] .sv-palette,
.sv-palette[data-theme='dark'],
.dark .sv-palette{${vars(SV_COLORS.dark)}}

.sv-num{font-variant-numeric:tabular-nums;}
.sv-hero-num{font-variant-numeric:proportional-nums;}
.sv-sta{font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;font-variant-numeric:tabular-nums;}
.sv-unit{font-size:0.85em;opacity:0.7;}

@media (prefers-reduced-motion:no-preference){
  .sv-anim{transition:height 160ms cubic-bezier(.2,0,0,1);}
  .sv-anim[data-resizing='true']{transition:none;}
}

/* Brush totals cross-fade when the scope changes (whole corridor <-> the
   brushed range): the figure has to read as "this is a different number",
   not as a digit that silently swapped. Keyed remount replays it. */
@keyframes sv-xfade{from{opacity:0.2;}to{opacity:1;}}
@media (prefers-reduced-motion:no-preference){
  .sv-xfade{animation:sv-xfade 90ms cubic-bezier(.2,0,0,1);}
}

/* These rules are unlayered, so they beat Tailwind v4's layered utilities.
   That is why every chip tone is expressed here rather than as a
   'border-amber-500/60' class that would silently lose. */
.sv-chip{display:inline-flex;align-items:center;gap:0.25rem;border-radius:9999px;
  border:1px solid var(--sv-grid);padding:0.05rem 0.5rem;line-height:1.25rem;}
.sv-chip--warn{border-color:var(--sv-warn);color:var(--sv-warn);}

/* Forced colours: map the palette onto system colours and let the UA repaint
   everything else. No 'forced-color-adjust:none' — opting out is precisely
   what a forced-colours user asked us not to do. Cut and fill stay
   distinguishable by side-of-zero, hatch and label (spec §5 A11y point 1);
   the chart's hatch patterns come with Task 11. */
@media (forced-colors:active){
  .sv-root,.sv-palette{
    --sv-cut:CanvasText;--sv-cut-wash:Canvas;
    /* LinkText, not Highlight: Highlight is the walk cursor, and mapping both
       onto it made the fill series and the cursor the same colour — the one
       pair a reader of a cut/fill chart must be able to tell apart. */
    --sv-fill:LinkText;--sv-fill-wash:Canvas;
    /* \`--sv-zero\` (the 3D ramp's midpoint) has no CSS custom property any
       more — nothing reads var(--sv-zero); the 3D bands read
       SV_COLORS.*.zero directly in JS instead. */
    --sv-clear:GrayText;
    /* Two DIFFERENT system inks, so the left and right lanes survive a
       greyscale / high-contrast render as two series rather than one. */
    --sv-xf-left:CanvasText;--sv-xf-right:LinkText;
    --sv-haul:GrayText;--sv-surface-ink:CanvasText;--sv-walk:Highlight;
    --sv-surface:Canvas;--sv-warn:CanvasText;--sv-grid:CanvasText;
    color:CanvasText;
  }
  .sv-chip{border-color:CanvasText;}
}
`;
